// src/components/slides/BackgroundSelect/BackgroundSelect.js
import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../../../context/AppContext';
import useAppContent from '../../../hooks/useAppContent';
import useAnalytics from '../../../hooks/useAnalytics';
import './BackgroundSelect.scss';

const BackgroundSelect = ({ onNext, onBack, canProceed }) => {
  const { state, actions } = useApp();
  const { appContent } = useAppContent();
  const [selectedBackground, setSelectedBackground] = useState(
    state.userInfo.selectedBackground || null
  );
  const [attemptedNext, setAttemptedNext] = useState(false);
  const hasTrackedRef = useRef(false);
  const { trackBackgroundSelectLoad, trackSpecialtyLoad } = useAnalytics();

  // Analytics
  useEffect(() => {
    if (!hasTrackedRef.current) {
      trackBackgroundSelectLoad();
      hasTrackedRef.current = true;
    }
  }, [trackBackgroundSelectLoad]);

  // Define gradients
  const gradientDefinitions = {
    space: {
      backgroundSize: '100% 200px',
      backgroundImage:
        'radial-gradient(84% 50% at 111% 82%, #7F499E85 0%, #073AFF00 100%),radial-gradient(54% 86% at 65% 38%, #001B6E96 11%, #073AFF00 100%),linear-gradient(0deg, #751A39 1%, #000524 100%)'
    },
    earth: {
      backgroundSize: '100% 200px',
      backgroundImage:
        'radial-gradient(53% 41% at 97% 7%, #D46B6A 0%, #FF000000 99%),radial-gradient(62% 63% at -2% 95%, #FDA777 1%, #FF000000 99%),radial-gradient(142% 91% at 111% 84%, #FB8541 0%, #D2442D 100%)'
    }
  };

  const getGradientData = (gradientType, fallbackType) => {
    const acfGradient =
      appContent?.acf?.[`background_${gradientType}_gradient`];
    if (acfGradient && typeof acfGradient === 'string') {
      return {
        background: acfGradient
      };
    }

    const gradientDef = gradientDefinitions[fallbackType];
    return {
      backgroundSize: gradientDef.backgroundSize,
      backgroundPosition: gradientDef.backgroundPosition,
      backgroundImage: gradientDef.backgroundImage
    };
  };

  // Map ACF data to gradient-based
  const slideData = {
    questionId: 'backgroundSelect',
    title:
      appContent?.acf?.background_select_title || 'Choose your background.',
    description:
      appContent?.acf?.background_select_description ||
      'Space begins just 62 miles above us, at the Kármán Line.',
    dividerText:
      appContent?.acf?.background_select_divider_text || 'Kármán Line 62 miles',
    bgOneTitle: appContent?.acf?.background_1_title || 'Space',
    bgOneGradient: getGradientData('1', 'space'),
    bgOneClass: appContent?.acf?.background_1_class || 'space-theme',
    bgTwoTitle: appContent?.acf?.background_2_title || 'Earth',
    bgTwoGradient: getGradientData('2', 'earth'),
    bgTwoClass: appContent?.acf?.background_2_class || 'earth-theme'
  };

  // Create background options from ACF
  const backgroundOptions = [
    {
      id: 'background-1',
      name: slideData.bgOneTitle,
      gradient: slideData.bgOneGradient,
      class: slideData.bgOneClass,
      description: `Choose ${slideData.bgOneTitle} theme for your boarding pass`
    },
    {
      id: 'background-2',
      name: slideData.bgTwoTitle,
      gradient: slideData.bgTwoGradient,
      class: slideData.bgTwoClass,
      description: `Choose ${slideData.bgTwoTitle} theme for your boarding pass`
    }
  ];

  useEffect(() => {
    const contextBackground = state.userInfo.selectedBackground || null;
    if (contextBackground !== selectedBackground) {
      setSelectedBackground(contextBackground);
    }
  }, [state.userInfo.selectedBackground]);

  const handleBackgroundSelect = (background) => {
    if (background.id !== selectedBackground?.id) {
      setSelectedBackground(background);
      actions.updateUserInfo({ selectedBackground: background });
      setAttemptedNext(false);
    }
  };

  useEffect(() => {
    if (!canProceed && !selectedBackground) {
      setAttemptedNext(true);
    }
  }, [canProceed, selectedBackground]);

  return (
    <div className="slide background-select question-slide">
      <div className="question-slide__container">
        <div className="question-slide__content">
          <p className="question-slide__description">{slideData.description}</p>
          <h2 className="question-slide__title">{slideData.title}</h2>
          <div className="background-select__options">
            <div
              className={`background-card ${selectedBackground?.id === backgroundOptions[0].id ? 'selected' : ''} ${backgroundOptions[0].class || ''}`}
              onClick={() => handleBackgroundSelect(backgroundOptions[0])}
            >
              <div className="background-card__preview">
                <div
                  className="background-card__gradient"
                  style={backgroundOptions[0].gradient}
                >
                  <p className="background-card__name">
                    {backgroundOptions[0].name}
                  </p>
                </div>
              </div>
            </div>

            {slideData.dividerText && (
              <div className="background-select__divider">
                <span className="background-select__divider-text">
                  {slideData.dividerText}
                </span>
              </div>
            )}

            <div
              className={`background-card ${selectedBackground?.id === backgroundOptions[1].id ? 'selected' : ''} ${backgroundOptions[1].class || ''}`}
              onClick={() => handleBackgroundSelect(backgroundOptions[1])}
            >
              <div className="background-card__preview">
                <div
                  className="background-card__gradient"
                  style={backgroundOptions[1].gradient}
                >
                  <p className="background-card__name">
                    {backgroundOptions[1].name}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BackgroundSelect;
